import { useEffect, useRef, useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH, checkPassword } from '../../lib/passwordRules';
import {
  AuthErrorRegion,
  AuthPasswordField,
  AuthSubmit,
  PasswordStrengthBar,
} from './AuthPrimitives';

const ERROR_REGION_ID = 'set-password-error';

interface SetPasswordFormProps {
  /**
   * The account's email when the link carried one, '' otherwise. Only used to
   * reject a password containing the local part — the pages cannot look it up,
   * because they are forbidden from calling anything on mount.
   */
  email: string;
  submitLabel: string;
  busyLabel: string;
  busy: boolean;
  /** Server-side failure from the page's submit. */
  error: string | null;
  onSubmit: (password: string) => void;
}

/**
 * The new-password half of both /reset and /accept-invite.
 *
 * Presentational and API-free by design: the network call stays in each page so
 * the source pins that assert "these pages fire nothing on mount" keep meaning
 * something. If the fetch moved in here, those pins would pass while the
 * property they protect quietly died.
 */
export function SetPasswordForm({
  email,
  submitLabel,
  busyLabel,
  busy,
  error,
  onSubmit,
}: SetPasswordFormProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const verdict = checkPassword(password, email);
  const shown = localError ?? error;

  useEffect(() => {
    if (shown) errorRef.current?.focus();
  }, [shown]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    if (!verdict.ok) {
      // `message` is null only for an untouched field, which is what the
      // fallback covers — someone pressing Enter on an empty form.
      setLocalError(verdict.message ?? `Use at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setLocalError("Those two passwords don't match.");
      return;
    }
    setLocalError(null);
    onSubmit(password);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="auth-fields">
        <div>
          <AuthPasswordField
            label="New password"
            name="new-password"
            required
            autoFocus
            // `new-password` is what tells a password manager to OFFER one
            // rather than fill the old one in.
            autoComplete="new-password"
            readOnly={busy}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby={shown ? ERROR_REGION_ID : undefined}
            hint={password.length > 0 ? verdict.message : null}
            hintIsError
          />
          <PasswordStrengthBar strength={verdict.strength} />
        </div>

        <AuthPasswordField
          label="Confirm password"
          name="confirm-password"
          required
          autoComplete="new-password"
          readOnly={busy}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          aria-describedby={shown ? ERROR_REGION_ID : undefined}
          hint={confirm.length > 0 && confirm !== password ? "Doesn't match yet." : null}
          hintIsError
        />
      </div>

      {shown ? (
        <AuthErrorRegion ref={errorRef} id={ERROR_REGION_ID}>
          {shown}
        </AuthErrorRegion>
      ) : null}

      <AuthSubmit busy={busy} label={submitLabel} busyLabel={busyLabel} />
    </form>
  );
}
